import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import type { MonthlyCalendarHeatmapProps, CalendarDayData } from '../../../types';
import { CalendarHeatmapCell, LEAVE_LABELS } from '../../molecules/CalendarHeatmapCell';
import { Select, VisuallyHidden } from '../../atoms';
import { getLocalToday } from '../../../lib/format';
import { placeSpanLabels, type LabelSlot } from './placeSpanLabels';
import { foldOthersNames } from './foldOthersNames';
import * as s from './MonthlyCalendarHeatmap.css';

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

/** 오늘 기준 뒤·앞으로 열어 두는 개월 수. 지난 기록을 되돌아보고 다음 일정을 적을 만큼만. */
const MONTHS_BACK = 12;
const MONTHS_FORWARD = 12;

const monthKey = (year: number, month: number) => `${year}-${String(month).padStart(2, '0')}`;

/**
 * 고를 수 있는 달 목록.
 *
 * 창은 **오늘** 기준으로 고정한다 — 보고 있는 달을 기준으로 잡으면 이동할 때마다 목록이
 * 따라 움직여 어디까지 갈 수 있는지가 흔들린다. 다만 보고 있는 달이 창 밖이면(딥링크·오래된
 * 상태) 그 달이 목록에 없어 선택이 빈 값으로 떨어지므로 범위를 그쪽까지 넓힌다.
 */
function buildMonthOptions(year: number, month: number, todayStr: string) {
  const todayIndex = Number(todayStr.slice(0, 4)) * 12 + (Number(todayStr.slice(5, 7)) - 1);
  const viewIndex = year * 12 + (month - 1);
  const from = Math.min(todayIndex - MONTHS_BACK, viewIndex);
  const to = Math.max(todayIndex + MONTHS_FORWARD, viewIndex);

  const options = [];
  for (let index = from; index <= to; index++) {
    const optionYear = Math.floor(index / 12);
    const optionMonth = (index % 12) + 1;
    options.push({
      value: monthKey(optionYear, optionMonth),
      label: `${optionYear}년 ${optionMonth}월`,
    });
  }
  return options;
}

/** 범례는 글자가 없는 표시만. 공휴일명·휴가자 이름은 셀이 이미 적는다. */
function monthHasOwnLeave(days: CalendarDayData[]): boolean {
  return days.some((day) => day.leave != null);
}

function LegendPair({ swatchClass, label }: { swatchClass: string; label: string }) {
  return (
    <>
      <dt className={s.legendTerm}>
        <span className={swatchClass} aria-hidden />
      </dt>
      <dd className={s.legendLabel}>{label}</dd>
    </>
  );
}

export function MonthlyCalendarHeatmap({
  year,
  month,
  days,
  selectedDate,
  onSelectDate,
  onChangeMonth,
}: MonthlyCalendarHeatmapProps) {
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
  const todayStr = getLocalToday();
  const monthPrefix = `${monthKey(year, month)}-`;
  const [focusedDate, setFocusedDate] = useState<string | null>(null);
  const pendingFocusDate = useRef<string | null>(null);
  const activeDate = focusedDate?.startsWith(monthPrefix)
    ? focusedDate
    : selectedDate?.startsWith(monthPrefix)
      ? selectedDate
      : todayStr.startsWith(monthPrefix)
        ? todayStr
        : `${monthPrefix}01`;

  const [othersDetailDate, setOthersDetailDate] = useState<string | null>(null);
  const othersDetailId = useId();
  const othersDetailRef = useRef<HTMLElement>(null);

  // 기간 이름 비켜 놓기는 실제 칸 폭으로 계산한다 — 창 크기에 따라 칸이 52~80px 로 바뀐다
  const gridBodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const date = pendingFocusDate.current;
    if (!date?.startsWith(monthPrefix)) return;
    gridBodyRef.current
      ?.querySelector<HTMLButtonElement>(`[data-calendar-date="${date}"]`)
      ?.focus();
    pendingFocusDate.current = null;
  }, [monthPrefix]);
  // 칸 폭·줄 높이도 같은 측정에서 얻는다 — 줄이 낮으면 휴가자 이름을 배지로 접는다(foldOthersNames)
  const [gridBodySize, setGridBodySize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = gridBodyRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry)
        setGridBodySize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const cellWidth = gridBodySize.width / 7;
  const rowCount = Math.ceil((firstDayOfMonth + daysInMonth) / 7);
  // gridBody 는 줄 사이 gap 1px 에 트랙을 1fr 로 나눈다
  const rowHeight = (gridBodySize.height - (rowCount - 1)) / rowCount;

  // 고정 높이 윈도우(HoloPane body) 안에서는 상세 패널이 폴드 아래에 열려 보이지 않을 수 있음
  useEffect(() => {
    if (othersDetailDate) othersDetailRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [othersDetailDate]);

  // 이름이 달린 기간 선마다 옆으로 미는 거리 — `날짜|줄` → px
  const labelOffsets = useMemo(() => {
    const slots: (LabelSlot & { key: string })[] = [];
    for (const d of days) {
      const day = Number(d.date.slice(8, 10));
      const index = firstDayOfMonth + day - 1;
      for (const span of d.scheduleSpans ?? []) {
        if (!span.label) continue;
        slots.push({
          key: `${d.date}|${span.lane}`,
          row: Math.floor(index / 7),
          col: index % 7,
          lane: span.lane,
          label: span.label,
        });
      }
    }
    if (slots.length === 0 || cellWidth === 0) return new Map<string, number>();
    const offsets = placeSpanLabels(slots, cellWidth);
    return new Map(slots.map((slot, i) => [slot.key, offsets[i] ?? 0]));
  }, [days, firstDayOfMonth, cellWidth]);

  const handleDateKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, date: string) => {
      const day = Number(date.slice(8, 10));
      const weekday = (firstDayOfMonth + day - 1) % 7;
      let nextDay: number;
      switch (event.key) {
        case 'ArrowLeft':
          nextDay = day - 1;
          break;
        case 'ArrowRight':
          nextDay = day + 1;
          break;
        case 'ArrowUp':
          nextDay = day - 7;
          break;
        case 'ArrowDown':
          nextDay = day + 7;
          break;
        case 'Home':
          nextDay = day - weekday;
          break;
        case 'End':
          nextDay = day + 6 - weekday;
          break;
        case 'PageUp':
        case 'PageDown': {
          if (!onChangeMonth) return;
          event.preventDefault();
          const next = new Date(year, month - 1 + (event.key === 'PageUp' ? -1 : 1), 1);
          const nextYear = next.getFullYear();
          const nextMonth = next.getMonth() + 1;
          const targetDay = Math.min(day, new Date(nextYear, nextMonth, 0).getDate());
          const targetDate = `${monthKey(nextYear, nextMonth)}-${String(targetDay).padStart(2, '0')}`;
          setFocusedDate(targetDate);
          pendingFocusDate.current = targetDate;
          onChangeMonth(nextYear, nextMonth);
          return;
        }
        default:
          return;
      }
      event.preventDefault();
      const targetDate = `${monthPrefix}${String(Math.max(1, Math.min(nextDay, daysInMonth))).padStart(2, '0')}`;
      setFocusedDate(targetDate);
      gridBodyRef.current
        ?.querySelector<HTMLButtonElement>(`[data-calendar-date="${targetDate}"]`)
        ?.focus();
    },
    [year, month, firstDayOfMonth, daysInMonth, monthPrefix, onChangeMonth, setFocusedDate]
  );

  const calendarCells = useMemo(() => {
    const dayDataMap = new Map<string, CalendarDayData>(days.map((d) => [d.date, d]));
    const cells = [];
    const monthPrefix = `${year}-${String(month).padStart(2, '0')}-`;

    for (let i = 0; i < firstDayOfMonth; i++) {
      cells.push(<div key={`empty-${i}`} className={s.emptyCell} />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${monthPrefix}${String(day).padStart(2, '0')}`;
      const dayOfWeek = (firstDayOfMonth + day - 1) % 7;
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isToday = dateStr === todayStr;
      const dayData = dayDataMap.get(dateStr);
      const othersCount = dayData?.othersOnLeave?.length ?? 0;
      const hasOthers = othersCount > 0;
      // 아직 재지 못했으면(첫 렌더·ResizeObserver 없음) 접지 않는다
      const othersFolded =
        rowHeight > 0 &&
        foldOthersNames({
          gridWidth: gridBodySize.width,
          rowHeight,
          othersCount,
          holiday: !!dayData?.holiday,
          payday: !!dayData?.payday,
        });
      // 클릭 게이트는 두지 않는다 — 근태 신호 유무와 무관하게 모든 날이 열린다.
      // 신호를 조건으로 걸면 일정을 새로 적으려는 날(정의상 비어 있다)의 진입점이 0이 된다.
      // 빈 날의 상세는 DayDetailPanel 이 자체 empty 문구로 받는다.

      cells.push(
        <CalendarHeatmapCell
          key={dateStr}
          date={dateStr}
          hoursWorked={dayData?.hoursWorked ?? 0}
          workMs={dayData?.workMs}
          isAbsent={dayData?.isAbsent}
          tabIndex={onSelectDate ? (dateStr === activeDate ? 0 : -1) : undefined}
          onFocus={onSelectDate ? () => setFocusedDate(dateStr) : undefined}
          onKeyDown={onSelectDate ? (event) => handleDateKeyDown(event, dateStr) : undefined}
          isToday={isToday}
          isWeekend={isWeekend}
          leave={dayData?.leave}
          correction={dayData?.correction}
          offDayRequest={dayData?.offDayRequest}
          othersOnLeave={dayData?.othersOnLeave}
          hasSchedule={dayData?.hasSchedule}
          holiday={dayData?.holiday}
          payday={dayData?.payday}
          deadlineCount={dayData?.deadlineCount}
          crunch={dayData?.crunch}
          scheduleSpans={dayData?.scheduleSpans?.map((span) =>
            span.label
              ? { ...span, labelOffset: labelOffsets.get(`${dateStr}|${span.lane}`) ?? 0 }
              : span
          )}
          othersFolded={othersFolded}
          othersExpanded={hasOthers && othersDetailDate === dateStr}
          othersDetailId={hasOthers ? othersDetailId : undefined}
          onToggleOthers={
            hasOthers
              ? () => setOthersDetailDate((prev) => (prev === dateStr ? null : dateStr))
              : undefined
          }
          onSelectDate={onSelectDate ? () => onSelectDate(dateStr) : undefined}
        />
      );
    }

    return cells;
  }, [
    year,
    month,
    daysInMonth,
    firstDayOfMonth,
    todayStr,
    days,
    othersDetailDate,
    othersDetailId,
    onSelectDate,
    labelOffsets,
    activeDate,
    handleDateKeyDown,
    setFocusedDate,
    gridBodySize.width,
    rowHeight,
  ]);

  const monthOptions = useMemo(
    () => buildMonthOptions(year, month, todayStr),
    [year, month, todayStr]
  );

  const hasLeaveLegend = useMemo(() => monthHasOwnLeave(days), [days]);

  const othersDetail = useMemo(() => {
    if (!othersDetailDate) return null;
    const entries = days.find((d) => d.date === othersDetailDate)?.othersOnLeave ?? [];
    if (entries.length === 0) return null;
    return {
      date: othersDetailDate,
      groups: [
        { label: '우리팀', entries: entries.filter((e) => e.isMyTeam) },
        { label: '전사', entries: entries.filter((e) => !e.isMyTeam) },
      ].filter((g) => g.entries.length > 0),
    };
  }, [othersDetailDate, days]);

  return (
    /* 카드가 눌리면 이 영역이 스크롤한다(container.overflowY 주석 참조). 스크롤되는 영역은
       키보드로 닿을 수 있어야 한다 — 안쪽 그리드는 셀이 포커스를 받지 않아 axe 의
       scrollable-region-focusable 을 만족시키지 못한다. 이름은 안쪽 h2/월 선택이 든다. */
    <section
      className={s.container}
      aria-label="월별 근태 달력"
      tabIndex={onSelectDate ? undefined : 0}
    >
      <span className={s.glowEffect} aria-hidden />
      <span className={s.glowBleedEffect} aria-hidden />
      {/* 월 이동은 화살표가 아니라 목록에서 고른다 — 화살표는 한 칸씩만 움직이는데다
          제목에서 떨어져 앉아 무엇을 가리키는지 읽히지 않았다(2026-08-25 사용자 피드백).
          제목 줄 자체는 그대로 둔다 — 줄을 새로 만들면 그리드 읽기 하한(282)을 밀어낸다. */}
      {onChangeMonth ? (
        <div className={`${s.titleRow} ${s.titleRowSelectable}`}>
          {/* 고르는 컨트롤이 달을 이미 보여주므로 제목은 접근성 트리에만 남긴다 —
              둘 다 보이면 같은 달이 화면에 두 번 적힌다. */}
          <VisuallyHidden as="h2">
            {year}년 {month}월
          </VisuallyHidden>
          <Select
            selectSize="sm"
            className={s.monthSelect}
            aria-label="표시할 달"
            options={monthOptions}
            value={monthKey(year, month)}
            onChange={(e) => {
              const [nextYear, nextMonth] = e.target.value.split('-').map(Number);
              onChangeMonth(nextYear as number, nextMonth as number);
            }}
          />
        </div>
      ) : (
        <div className={s.titleRow}>
          <h2 className={s.title}>
            {year}년 {month}월
          </h2>
        </div>
      )}
      <div className={s.grid} role="group" aria-label={`${year}년 ${month}월 근무 현황`}>
        <div className={s.weekdayHeader}>
          {WEEKDAY_LABELS.map((label) => (
            <div key={label} className={s.weekdayLabel}>
              {label}
            </div>
          ))}
        </div>
        <div ref={gridBodyRef} className={s.gridBody}>
          {calendarCells}
        </div>
      </div>
      {othersDetail && (
        <section
          id={othersDetailId}
          ref={othersDetailRef}
          className={s.othersDetail}
          aria-labelledby={`${othersDetailId}-title`}
        >
          <header className={s.othersDetailHeader}>
            <h3 id={`${othersDetailId}-title`} className={s.othersDetailTitle}>
              {Number(othersDetail.date.slice(5, 7))}월 {Number(othersDetail.date.slice(8, 10))}일
              휴가자
            </h3>
            <button
              type="button"
              className={s.othersDetailClose}
              aria-label="휴가자 상세 닫기"
              onClick={() => setOthersDetailDate(null)}
            >
              ×
            </button>
          </header>
          {othersDetail.groups.map((group) => (
            <div key={group.label} className={s.othersGroup}>
              <h4 className={s.othersGroupTitle}>{group.label}</h4>
              <ul className={s.othersList}>
                {group.entries.map((entry) => (
                  <li key={`${entry.userId}-${entry.leaveType}`} className={s.othersItem}>
                    {entry.userName} · {LEAVE_LABELS[entry.leaveType] ?? entry.leaveType}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      )}
      <dl className={s.legend} aria-label="범례">
        <div className={s.legendSection}>
          <LegendPair swatchClass={s.legendGradient} label="근무" />
        </div>
        {hasLeaveLegend && (
          <div className={s.legendSection}>
            <LegendPair swatchClass={`${s.legendStripe} ${s.legendLeave}`} label="휴가" />
          </div>
        )}
      </dl>
    </section>
  );
}
